import PropTypes from 'prop-types'
import React from 'react'

import { configureDealerAddress, formatPhoneNumber } from '@/utils/helpers'

const LINK = 'underline-offset-2 hover:underline'

/**
 * Items on one line, split by a faint dot. On a phone they stack instead, left-aligned,
 * since a wrapped line leaves a dot hanging at its end.
 */
const Line = ({ children }) => (
  <div className="flex flex-col items-start gap-y-0.5 text-sm text-white/90 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-2">
    {React.Children.toArray(children)
      .filter(Boolean)
      .map((child, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {index > 0 && (
            <span aria-hidden="true" className="hidden text-white/40 sm:inline">
              ·
            </span>
          )}
          {child}
        </React.Fragment>
      ))}
  </div>
)

Line.propTypes = {
  children: PropTypes.node,
}

/**
 * The dealer's contact details, on one line so the dealer header stays a slim band
 * (WARP-920). Dealer service users on a WPC-managed dealer see their dealer admin's
 * details instead of the store's.
 */
export const ContactDetailsSection = ({ dealer, isROServiceUser, loading = false }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-end gap-2">
        <div className="h-3 w-48 animate-pulse rounded-sm bg-white/20" />
        <div className="h-3 w-28 animate-pulse rounded-sm bg-white/20" />
      </div>
    )
  }

  if (!dealer) return null

  if (isROServiceUser && !dealer.isDealerManaged) {
    // Nothing to show is nothing, not a skeleton that never resolves.
    if (!dealer.admin && !dealer.adminPhoneNumber && !dealer.adminEmail) return null

    return (
      <Line>
        {dealer.admin && <span className="font-medium text-white">{dealer.admin}</span>}
        {dealer.adminPhoneNumber && (
          <a
            aria-label={`Call ${dealer.admin} at ${formatPhoneNumber(dealer.adminPhoneNumber)}`}
            className={LINK}
            href={`tel:${dealer.adminPhoneNumber}`}
          >
            {formatPhoneNumber(dealer.adminPhoneNumber)}
          </a>
        )}
        {dealer.adminEmail && (
          <a
            aria-label={`Email ${dealer.admin} at ${dealer.adminEmail}`}
            className={`${LINK} break-all`}
            href={`mailto:${dealer.adminEmail}`}
          >
            {dealer.adminEmail}
          </a>
        )}
      </Line>
    )
  }

  const address = [dealer.address, dealer.address2, configureDealerAddress(dealer)]
    .filter(Boolean)
    .join(', ')

  if (!address && !dealer.phoneNumber) return null

  return (
    <Line>
      {address && <span>{address}</span>}
      {dealer.phoneNumber && (
        <a
          aria-label={`Call ${dealer.name} at ${formatPhoneNumber(dealer.phoneNumber)}`}
          className={LINK}
          href={`tel:${dealer.phoneNumber}`}
        >
          {formatPhoneNumber(dealer.phoneNumber)}
        </a>
      )}
    </Line>
  )
}

ContactDetailsSection.propTypes = {
  dealer: PropTypes.object,
  isROServiceUser: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
}
